import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useMsal } from "@azure/msal-react";
import { toast } from "react-toastify";
import FeatureTabs, { FeatureTab } from "../../../shared/ui/FeatureTabs";
import FeatureInformationPanel from "../../../shared/ui/FeatureInformationPanel";
import ActivityLogTable, { ActivityLogTableItem } from "../../../shared/ui/ActivityLogTable";
import ConfirmActionDialog from "../../../shared/ui/ConfirmActionDialog";
import ResourceContextPanel from "../../../shared/ui/ResourceContextPanel";
import { btn } from "../../../shared/styles/buttonStyles";
import { useLoading } from "../../../shared/state/LoadingContext";
import { PaginatedResponse } from "../../../shared/types/pagination";
import { getApiErrorMessage } from "../../../shared/utils/apiError";
import {
  ActivityFeedEntry,
  ACTIVITY_API_PAGE_SIZE,
  ACTIVITY_LOG_PAGE_SIZE,
  ACTIVITY_LOG_POLL_INTERVAL_MS,
  ExecutionActivityEntry,
  isExecutionActive,
  mapExecutionFeedEntries,
  parseActivityDetailsJson,
  sortActivityFeedEntries,
} from "../../../shared/utils/activityFeed";
import { formatActivityDuration } from "../../../shared/utils/formatActivityDuration";
import { formatAuditDate, formatAuditTime24Hour } from "../../../shared/utils/formatAuditTime24Hour";
import { getAccessToken } from "../../../shared/auth/getAccessToken";

interface ArchiveDetails {
  instanceId?: string;
  sourceContainer?: string;
  destinationContainer?: string;
  archivePath?: string;
  destinationBlob?: string;
  blobCount?: number;
  bytesCopied?: number;
  sourceSize?: number;
  archiveSize?: number;
  deleteSourceRequested?: boolean;
  sourceDeletionStarted?: boolean;
  sourceContainerDeleted?: boolean;
}

const formatBytes = (value?: number) => {
  if (value === undefined || value === null) {
    return "-";
  }

  if (value < 1024) {
    return `${value} B`;
  }

  if (value < 1024 * 1024) {
    return `${(value / 1024).toFixed(1)} KB`;
  }

  if (value < 1024 * 1024 * 1024) {
    return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  }

  return `${(value / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const getDetails = (entry: ActivityFeedEntry): ArchiveDetails =>
  parseActivityDetailsJson<ArchiveDetails>(entry.detailsJson) ?? {};

const getArchiveFileName = (destinationBlob: string) =>
  destinationBlob.split("/").pop() ?? destinationBlob;

const getContainerName = (containerName?: string) =>
  containerName?.toLowerCase() ?? "-";

const isDeleteAction = (entry: ActivityFeedEntry) =>
  entry.actionType.toLowerCase() === "delete_source_container";

const buildStorageAccountPortalUrl = (resourceGroup: string, accountName: string) =>
  `https://portal.azure.com/#resource/subscriptions/ac5bf081-7ac3-4c11-8080-dd2680009952/resourceGroups/${resourceGroup}/providers/Microsoft.Storage/storageAccounts/${accountName}/overview`;

const xunoDataPortalUrl = buildStorageAccountPortalUrl("XunoData-rg", "xunodata");
const xunoHostedDataPortalUrl = buildStorageAccountPortalUrl("XunoHosting", "xunohosteddata");

const DeleteConfirmationNotice = ({ instanceId }: { instanceId: string }) => (
  <div role="alert" className="workspace-subcard flex items-center gap-2 border border-red-200 bg-red-50 px-3 py-2 text-red-900">
    <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5 shrink-0 fill-none stroke-current stroke-2 text-red-700">
      <path d="M4 7h16" strokeLinecap="round" />
      <path d="M10 11v6M14 11v6" strokeLinecap="round" />
      <path d="M6 7l1 13h10l1-13M9 7V4h6v3" strokeLinejoin="round" />
    </svg>
    <span>Type the Instance ID below to confirm: <span className="font-semibold">{instanceId}</span></span>
  </div>
);

const getSourceContainerStatus = (entry: ActivityFeedEntry, details: ArchiveDetails) => {
  const status = entry.status.toLowerCase();

  if (!isDeleteAction(entry) && !details.deleteSourceRequested) {
    return "retained";
  }

  if (status === "succeeded") {
    return details.sourceContainerDeleted ? "deleted" : "deletion not completed";
  }

  if (status === "failed") {
    return isDeleteAction(entry) || details.sourceDeletionStarted
      ? "deletion not completed"
      : "retained";
  }

  return "scheduled for deletion";
};

const getActionText = (entry: ActivityFeedEntry) => {
  if (isDeleteAction(entry)) {
    return "Delete Container";
  }

  return getDetails(entry).deleteSourceRequested
    ? "Backup and Delete Container"
    : "Backup Container";
};

const getChangeLines = (entry: ActivityFeedEntry): string[] => {
  const details = getDetails(entry);

  if (isDeleteAction(entry)) {
    const deleteLines = [`Source: xunodata/${getContainerName(details.sourceContainer)}`];

    if (details.blobCount !== undefined) {
      deleteLines.push(`Source blobs: ${details.blobCount}`);
    }

    if (details.sourceSize !== undefined) {
      deleteLines.push(`Source size: ${formatBytes(details.sourceSize)}`);
    }

    deleteLines.push(`Source container: ${getSourceContainerStatus(entry, details)}`);

    return deleteLines;
  }

  const lines = [
    `Source: xunodata/${getContainerName(details.sourceContainer)}`,
    `Destination: xunohosteddata/${getContainerName(details.destinationContainer)}${details.archivePath ? `/${details.archivePath.toLowerCase()}` : ""}`,
  ];

  if (details.destinationBlob) {
    lines.push(`File: ${getArchiveFileName(details.destinationBlob)}`);
  }

  if (details.blobCount !== undefined) {
    lines.push(`Source blobs: ${details.blobCount}`);
  }

  if (details.bytesCopied !== undefined) {
    lines.push(`Source size: ${formatBytes(details.bytesCopied)}`);
  }

  // Worker updates archiveSize while running, so this doubles as progress
  if (details.archiveSize !== undefined) {
    lines.push(`ZIP size: ${formatBytes(details.archiveSize)}`);
  }

  lines.push(`Source container: ${getSourceContainerStatus(entry, details)}`);

  return lines;
};

const BlobArchive = () => {
  const { instance, accounts } = useMsal();
  const { setLoading } = useLoading();
  const apiUrl = `${process.env.REACT_APP_API_URL}/api/xuno`;

  const [instanceId, setInstanceId] = useState("");
  const [activityFeedEntries, setActivityFeedEntries] = useState<ActivityFeedEntry[]>([]);
  const [activityLogsLoading, setActivityLogsLoading] = useState(false);
  const [activityLogsPage, setActivityLogsPage] = useState(1);
  const [activityLogsTotalPages, setActivityLogsTotalPages] = useState(1);
  const [activityLogsTotalCount, setActivityLogsTotalCount] = useState(0);
  const [hasActiveExecutions, setHasActiveExecutions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteSourceContainer, setDeleteSourceContainer] = useState(false);
  const [showBackupConfirmation, setShowBackupConfirmation] = useState(false);
  const [backupDeleteConfirmationInstanceId, setBackupDeleteConfirmationInstanceId] = useState("");
  const [backupSourceCheckLoading, setBackupSourceCheckLoading] = useState(false);
  const [backupSourceCheckError, setBackupSourceCheckError] = useState("");
  const [deleteInstanceId, setDeleteInstanceId] = useState("");
  const [deleteConfirmationInstanceId, setDeleteConfirmationInstanceId] = useState("");
  const [deleteSourceCheckLoading, setDeleteSourceCheckLoading] = useState(false);
  const [deleteSourceCheckError, setDeleteSourceCheckError] = useState("");
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [activeTab, setActiveTab] = useState<FeatureTab>("operations");
  const normalizedInstanceId = instanceId.trim().toLowerCase();
  const normalizedDeleteInstanceId = deleteInstanceId.trim().toLowerCase();


  const fetchActivityLogs = useCallback(async (page: number, showLoading = true) => {
    try {
      if (showLoading) {
        setActivityLogsLoading(true);
      }

      const accessToken = await getAccessToken();
      const executionItems = await axios.get<PaginatedResponse<ExecutionActivityEntry>>(`${apiUrl}/blob-archive/executions`, {
        params: { pageSize: ACTIVITY_API_PAGE_SIZE },
        headers: { Authorization: `Bearer ${accessToken}` },
      }).then((response) => response.data.items);
      const executionFeed = sortActivityFeedEntries(mapExecutionFeedEntries(executionItems));
      const totalCount = executionFeed.length;
      const totalPages = Math.max(1, Math.ceil(totalCount / ACTIVITY_LOG_PAGE_SIZE));
      const safePage = Math.min(Math.max(page, 1), totalPages);
      const pagedItems = executionFeed.slice(
        (safePage - 1) * ACTIVITY_LOG_PAGE_SIZE,
        safePage * ACTIVITY_LOG_PAGE_SIZE,
      );

      setActivityFeedEntries(pagedItems);
      setActivityLogsPage(safePage);
      setActivityLogsTotalPages(totalPages);
      setActivityLogsTotalCount(totalCount);
      setHasActiveExecutions(executionItems.some((item) => isExecutionActive(item.status)));
    } catch {
      // Polls stay silent and keep the last rows
      if (showLoading) {
        toast.error("Failed to load activity logs.");
      }
    } finally {
      if (showLoading) {
        setActivityLogsLoading(false);
      }
    }
  }, [apiUrl]);

  useEffect(() => {
    if (instance && accounts.length > 0) {
      void fetchActivityLogs(1);
    }
  }, [accounts, fetchActivityLogs, instance]);

  useEffect(() => {
    if (!hasActiveExecutions) {
      return;
    }

    // Poll while a run is active so the log shows live archive progress
    const intervalId = window.setInterval(() => {
      void fetchActivityLogs(activityLogsPage, false);
    }, ACTIVITY_LOG_POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [activityLogsPage, fetchActivityLogs, hasActiveExecutions]);

  // Same check before both confirmations, so a typo never reaches the queue
  const checkSourceContainer = async (
    sourceInstanceId: string,
    setCheckLoading: (loading: boolean) => void,
    setCheckError: (error: string) => void,
  ) => {
    setCheckError("");
    setCheckLoading(true);

    try {
      const accessToken = await getAccessToken();
      await axios.get(
        `${apiUrl}/blob-archive/source-container`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: { instanceId: sourceInstanceId },
        },
      );

      return true;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        setCheckError("Container validation is unavailable. Update the API and try again.");
      } else if (axios.isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403)) {
        setCheckError("Your session has expired. Sign in again.");
      } else {
        setCheckError(getApiErrorMessage(error, "Unable to reach the API. Try again."));
      }
    } finally {
      setCheckLoading(false);
    }

    return false;
  };

  const queueArchive = async () => {
    const confirmationInstanceId = backupDeleteConfirmationInstanceId.trim();

    if (!normalizedInstanceId) {
      toast.error("Enter an Instance ID.");
      return;
    }

    if (deleteSourceContainer && normalizedInstanceId !== confirmationInstanceId) {
      toast.error("Type the Instance ID exactly as shown to confirm deletion.");
      return;
    }

    try {
      setSubmitting(true);
      setLoading(true);
      const accessToken = await getAccessToken();
      await axios.post(
        `${apiUrl}/blob-archive`,
        {
          instanceId: normalizedInstanceId,
          deleteSourceContainer,
          confirmationInstanceId,
        },
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      toast.success(deleteSourceContainer
        ? "Backup and source deletion have been queued."
        : "Blob Storage Archive has been queued.");
      setInstanceId("");
      setDeleteSourceContainer(false);
      setBackupDeleteConfirmationInstanceId("");
      setShowBackupConfirmation(false);
      await fetchActivityLogs(1, false);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to queue Blob Storage Archive."));
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  const handleArchive = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!normalizedInstanceId) {
      toast.error("Enter an Instance ID.");
      return;
    }

    if (await checkSourceContainer(normalizedInstanceId, setBackupSourceCheckLoading, setBackupSourceCheckError)) {
      setBackupDeleteConfirmationInstanceId("");
      setShowBackupConfirmation(true);
    }
  };

  const queueSourceDeletion = async () => {
    const confirmationInstanceId = deleteConfirmationInstanceId.trim();

    if (!normalizedDeleteInstanceId || normalizedDeleteInstanceId !== confirmationInstanceId) {
      toast.error("Type the Instance ID exactly as shown to confirm deletion.");
      return;
    }

    try {
      setSubmitting(true);
      setLoading(true);
      const accessToken = await getAccessToken();
      await axios.post(
        `${apiUrl}/blob-archive/delete-source-container`,
        {
          instanceId: normalizedDeleteInstanceId,
          confirmationInstanceId,
        },
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      toast.success("Source container deletion has been queued.");
      setDeleteInstanceId("");
      setDeleteConfirmationInstanceId("");
      setShowDeleteConfirmation(false);
      await fetchActivityLogs(1, false);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to queue source container deletion."));
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  const handleSourceDeletion = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!normalizedDeleteInstanceId) {
      toast.error("Enter an Instance ID.");
      return;
    }

    setDeleteConfirmationInstanceId("");
    if (await checkSourceContainer(normalizedDeleteInstanceId, setDeleteSourceCheckLoading, setDeleteSourceCheckError)) {
      setShowDeleteConfirmation(true);
    }
  };

  const closeBackupConfirmation = useCallback(() => {
    setShowBackupConfirmation(false);
    setBackupDeleteConfirmationInstanceId("");
  }, []);

  const closeDeleteConfirmation = useCallback(() => setShowDeleteConfirmation(false), []);

  const activityLogRows: ActivityLogTableItem[] = activityFeedEntries.map((entry) => ({
    id: entry.id,
    dateText: formatAuditDate(entry.startedAtUtc),
    timeText: formatAuditTime24Hour(entry.startedAtUtc),
    durationText: formatActivityDuration(entry.durationMs),
    runningAtUtc: entry.runningAtUtc ?? null,
    actionText: getActionText(entry),
    byName: entry.actorName || "Unknown",
    byEmail: entry.actorEmail,
    status: entry.status,
    changeLines: getChangeLines(entry),
    errorMessage: entry.errorMessage,
  }));

  return (
    <div className="feature-workspace">
      <FeatureTabs activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === "information" && (
        <FeatureInformationPanel summary="Archive inactive school data from xunodata to xunohosteddata.">
          <h3 className="text-sm font-semibold text-slate-800">Blob Storage Archive</h3>
          <ul className="mt-3 list-disc space-y-1 pl-4 text-xs leading-5 text-slate-600">
            <li>Active school data is stored in <span className="font-semibold">xunodata</span>.</li>
            <li>When a school no longer uses Xuno, its container can be archived to <span className="font-semibold">xunohosteddata/{"{instanceId}"}</span> for lower-cost storage.</li>
            <li>All container files are stored in a ZIP under <span className="font-semibold">xunodata-backup</span>.</li>
            <li>The source container can be deleted automatically after a successful backup or separately.</li>
            <li>Execution: Background queues.</li>
          </ul>
        </FeatureInformationPanel>
      )}

      {activeTab === "operations" && (
        <div id="feature-panel-operations" role="tabpanel" aria-labelledby="feature-tab-operations" tabIndex={0}>
          <div className="mt-2 space-y-2">
            <ResourceContextPanel
              resourceName="xunodata"
              resourceNameLabel="Source Storage Account"
              portalUrl={xunoDataPortalUrl}
              resourceLink={{
                heading: "Destination Storage Account",
                label: "xunohosteddata",
                url: xunoHostedDataPortalUrl,
              }}
            />

            <div className="grid gap-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
              <section className="workspace-card min-w-0 w-full overflow-hidden border border-slate-200 bg-white px-5 py-4">
                <h3 className="text-sm font-bold leading-5 text-slate-800">Backup Container from xunodata</h3>
                <p className="mt-1 text-[11px] leading-4 text-slate-500">Archives the container to xunohosteddata as a ZIP backup.</p>
                <hr className="my-3 border-slate-200" />

                <form onSubmit={handleArchive} className="space-y-3">
                  <div className="flex flex-col gap-3 md:flex-row md:items-end">
                    <label htmlFor="blob-archive-instance" className="block min-w-0 flex-1 text-xs font-semibold leading-4 text-slate-700 md:flex-none md:w-[14rem]">
                      Instance ID
                      <input
                        id="blob-archive-instance"
                        value={instanceId}
                        onChange={(event) => {
                          setInstanceId(event.target.value);
                          setBackupSourceCheckError("");
                        }}
                        disabled={submitting || backupSourceCheckLoading}
                        placeholder="e.g. dev124"
                        className="mt-1 block w-full"
                      />
                    </label>
                    <div className="min-w-0 text-xs font-semibold leading-4 text-slate-700 md:w-[14rem]">
                      After backup
                      <label htmlFor="blob-archive-delete-source" className="mt-1 flex h-9 w-fit cursor-pointer items-center gap-2 font-normal text-slate-800">
                        <input
                          id="blob-archive-delete-source"
                          type="checkbox"
                          checked={deleteSourceContainer}
                          onChange={(event) => setDeleteSourceContainer(event.target.checked)}
                          disabled={submitting || backupSourceCheckLoading}
                          className="h-4 w-4 shrink-0 accent-[#1976d2]"
                        />
                        <span>Delete source container</span>
                      </label>
                    </div>
                  </div>
                  <button type="submit" disabled={submitting || backupSourceCheckLoading} className={`${btn.applySmall} w-[8.5rem] md:h-9`}>
                    {submitting ? "Queueing..." : backupSourceCheckLoading ? "Checking Container..." : "Run Backup"}
                  </button>
                </form>
                {backupSourceCheckError && (
                  <p role="alert" className="mt-2 text-[11px] leading-4 text-red-700">{backupSourceCheckError}</p>
                )}
              </section>

              <section className="workspace-card min-w-0 w-full overflow-hidden border border-slate-200 bg-white px-5 py-4">
                <h3 className="text-sm font-bold leading-5 text-slate-800">Delete Container from xunodata</h3>
                <p className="mt-1 text-[11px] leading-4 text-slate-500">Permanently removes the container and its files.</p>
                <hr className="my-3 border-slate-200" />

                <form onSubmit={handleSourceDeletion} className="space-y-3">
                  <label htmlFor="blob-archive-delete-instance" className="block min-w-0 flex-1 text-xs font-semibold leading-4 text-slate-700 md:flex-none md:w-[14rem]">
                    Instance ID
                    <input
                      id="blob-archive-delete-instance"
                      value={deleteInstanceId}
                      onChange={(event) => {
                        setDeleteInstanceId(event.target.value);
                        setDeleteSourceCheckError("");
                      }}
                      disabled={submitting || deleteSourceCheckLoading}
                      placeholder="e.g. dev124"
                      className="mt-1 block w-full focus:!border-red-500 focus:!ring-1 focus:!ring-red-500"
                    />
                  </label>
                  <button type="submit" disabled={submitting || deleteSourceCheckLoading} className={`${btn.destructiveSmall} w-[8.5rem] md:h-9`}>
                    {submitting ? "Queueing..." : deleteSourceCheckLoading ? "Checking Container..." : "Delete Container"}
                  </button>
                </form>
                {deleteSourceCheckError && (
                  <p role="alert" className="mt-2 text-[11px] leading-4 text-red-700">{deleteSourceCheckError}</p>
                )}
              </section>
            </div>

            <ActivityLogTable
              title="Activity Log"
              description="Tracks backup and source-container deletion activity."
              items={activityLogRows}
              loading={activityLogsLoading}
              page={activityLogsPage}
              totalPages={activityLogsTotalPages}
              totalCount={activityLogsTotalCount}
              onRefresh={() => fetchActivityLogs(activityLogsPage)}
              onPageChange={(pageNumber: number) => fetchActivityLogs(pageNumber)}
            />
          </div>
        </div>
      )}

      <ConfirmActionDialog
        open={showBackupConfirmation}
        title={deleteSourceContainer ? "Confirm Backup and Delete Source" : "Confirm Backup"}
        description={deleteSourceContainer ? "Back up and delete this source container?" : "Back up this source container?"}
        confirmLabel={deleteSourceContainer ? "Confirm Backup and Delete Source" : "Confirm Backup"}
        destructive={deleteSourceContainer}
        confirmDisabled={submitting || (deleteSourceContainer && normalizedInstanceId !== backupDeleteConfirmationInstanceId.trim())}
        onConfirm={() => void queueArchive()}
        onCancel={closeBackupConfirmation}
      >
        <div className="space-y-2 text-xs text-slate-700">
          <ul className="list-disc space-y-1 pl-4">
            <li>Creates the ZIP backup in xunohosteddata.</li>
            <li>{deleteSourceContainer
              ? "After the backup succeeds, permanently deletes the source container and its files (Azure blobs)."
              : "Keeps the source container and its files (Azure blobs) unchanged."}</li>
          </ul>
          {!deleteSourceContainer && (
            <div className="workspace-subcard space-y-1 border border-slate-200 bg-slate-50 p-3">
              <div><span className="font-medium text-slate-500">Instance ID: </span>{normalizedInstanceId}</div>
            </div>
          )}
          {deleteSourceContainer && (
            <>
              <DeleteConfirmationNotice instanceId={normalizedInstanceId} />
              <label htmlFor="blob-archive-backup-delete-confirmation" className="block font-semibold text-slate-700">
                Confirmation Instance ID
                <input
                  id="blob-archive-backup-delete-confirmation"
                  value={backupDeleteConfirmationInstanceId}
                  onChange={(event) => setBackupDeleteConfirmationInstanceId(event.target.value)}
                  disabled={submitting}
                  className="mt-1 block w-full focus:!border-red-500 focus:!ring-1 focus:!ring-red-500"
                  placeholder="Type the Instance ID again"
                />
              </label>
            </>
          )}
        </div>
      </ConfirmActionDialog>

      <ConfirmActionDialog
        open={showDeleteConfirmation}
        title="Confirm Delete Source Container"
        description="Delete this source container?"
        confirmLabel="Delete Source Container"
        destructive
        confirmDisabled={!normalizedDeleteInstanceId || normalizedDeleteInstanceId !== deleteConfirmationInstanceId.trim() || submitting}
        onConfirm={() => void queueSourceDeletion()}
        onCancel={closeDeleteConfirmation}
      >
        <div className="space-y-2 text-xs text-slate-700">
          <ul className="list-disc space-y-1 pl-4">
            <li>Permanently deletes the source container and its files (Azure blobs) from xunodata.</li>
            <li>This action cannot be undone.</li>
          </ul>
          <DeleteConfirmationNotice instanceId={normalizedDeleteInstanceId} />
          <label htmlFor="blob-archive-delete-confirmation" className="block font-semibold text-slate-700">
            Confirmation Instance ID
            <input
              id="blob-archive-delete-confirmation"
              value={deleteConfirmationInstanceId}
              onChange={(event) => setDeleteConfirmationInstanceId(event.target.value)}
              disabled={submitting}
              className="mt-1 block w-full focus:!border-red-500 focus:!ring-1 focus:!ring-red-500"
              placeholder="Type the Instance ID again"
            />
          </label>
        </div>
      </ConfirmActionDialog>
    </div>
  );
};

export default BlobArchive;
